import type { Abi, Account, Address, Client, Hex } from 'viem'

import { BaseError, ContractFunctionRevertedError, decodeErrorResult, RawContractError } from 'viem'
import { call, simulateCalls } from 'viem/actions'

import type { CallDetails, ResolvedCall, Step } from '../types.action-flow'

import { isExecutionAbortError, raceAbort } from './raceAbort'

/**
 * Wall-clock cap on the best-effort revert decode. The decode is `await`ed
 * inline before the provider dispatches the terminal `REVERT` state, so an
 * unbounded decode (viem transport timeout × retryCount on the pinned-block
 * replay) would keep the user on the pending spinner after
 * the tx has already reverted on-chain. Since a decoded name is optional (we
 * fail open to `undefined`), stop waiting past this deadline and let the
 * reverted UI render without the retry gate.
 */
const DECODE_TIMEOUT_MS = 2_500

type RevertCandidate = {
  /**
   * The calls to replay, in execution order. The **last** call is the one whose
   * revert we decode; any earlier calls are the atomic batch's preceding calls
   * (e.g. `approve`, `authorizeBundler`) that must run first so their on-chain
   * effects (allowance, bundler authorization) are applied before the failing
   * call. Replaying the failing call in isolation would revert on a missing
   * allowance / authorization instead of surfacing the real revert reason.
   */
  calls: ResolvedCall[]
  errorAbi: Abi
}

export async function decodeRevertedStepErrorName({
  step,
  callDetails,
  publicClient,
  account,
  blockNumber,
  signal
}: {
  step: Step
  callDetails: CallDetails
  publicClient: Client
  account: Account | Address
  blockNumber: bigint
  signal?: AbortSignal
}): Promise<string | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<undefined>(resolve => {
    timer = setTimeout(() => resolve(undefined), DECODE_TIMEOUT_MS)
  })

  const decode = decodeCandidates({ step, callDetails, publicClient, account, blockNumber, signal })
  // If the timeout wins the race, `decode` keeps running; swallow its late
  // rejection so it doesn't surface as an unhandled rejection.
  decode.catch(() => {})

  try {
    return await Promise.race([decode, timeout])
  } finally {
    clearTimeout(timer)
  }
}

async function decodeCandidates({
  step,
  callDetails,
  publicClient,
  account,
  blockNumber,
  signal
}: {
  step: Step
  callDetails: CallDetails
  publicClient: Client
  account: Account | Address
  blockNumber: bigint
  signal?: AbortSignal
}): Promise<string | undefined> {
  const candidates = getRevertCandidates(step, callDetails)

  for (const candidate of candidates) {
    const errorName = await decodeCandidateErrorName({
      candidate,
      account,
      blockNumber,
      publicClient,
      signal
    })
    if (errorName) return errorName
  }

  return undefined
}

function getRevertCandidates(step: Step, callDetails: CallDetails): RevertCandidate[] {
  // Replay the calls **actually submitted** — captured in `callDetails` at
  // execution time — never a fresh `getCall()`. A call's getter may depend on
  // mutable state (it is resolved once, at submit time, by `buildCallDetails`),
  // so re-resolving here after the tx mined could produce different calldata /
  // value and decode a different error than the one that reverted on-chain. The
  // per-call `errorAbi` is a static ABI, so it is safe to read from the step.
  switch (step.type) {
    case 'singleTx':
      return step.errorAbi && 'call' in callDetails
        ? [{ calls: [callDetails.call], errorAbi: step.errorAbi }]
        : []
    case 'batchTx': {
      if (!('calls' in callDetails)) return []
      const { calls } = callDetails
      // For each call that declares an error ABI, build a candidate that replays
      // that call together with all calls before it in the batch — an EIP-5792 /
      // Safe batch executes atomically, so the allowance/authorization set by
      // the preceding approve/authorize calls is not on chain and must be
      // replayed for the failing call to reproduce its real revert.
      return step.callRequests.flatMap((callRequest, index) =>
        callRequest.errorAbi
          ? [{ calls: calls.slice(0, index + 1), errorAbi: callRequest.errorAbi }]
          : []
      )
    }
    case 'signature':
      return []
  }
}

async function decodeCandidateErrorName({
  candidate,
  publicClient,
  account,
  blockNumber,
  signal
}: {
  candidate: RevertCandidate
  publicClient: Client
  account: Account | Address
  blockNumber: bigint
  signal?: AbortSignal
}): Promise<string | undefined> {
  const rawData = await getRawRevertData({
    candidate,
    publicClient,
    account,
    blockNumber,
    signal
  })

  if (!rawData) return undefined

  try {
    return decodeErrorResult({ abi: candidate.errorAbi, data: rawData }).errorName
  } catch {
    return undefined
  }
}

// Replay the reverted call(s) at the **mined block** only. `latest`-state
// replays are deliberately not consulted: the tx reverted with no state change,
// but the book keeps moving, so a `latest` replay can surface a *different*
// book-moved error (e.g. `OutOfOffers`/`UnitsToo*`) that did not cause the mined
// revert. Since `errorName` gates causal user-facing copy, only the pinned-block
// replay is trustworthy evidence — anything the pinned replay can't decode
// (including an RPC that can't serve the historical call) fails open.
async function getRawRevertData({
  candidate,
  publicClient,
  account,
  blockNumber,
  signal
}: {
  candidate: RevertCandidate
  publicClient: Client
  account: Account | Address
  blockNumber: bigint
  signal?: AbortSignal
}): Promise<Hex | undefined> {
  const targetCall = candidate.calls.at(-1)
  if (!targetCall) return undefined

  // Single call: a standalone `eth_call` replay is enough and is supported by
  // every RPC. Covers `singleTx` steps (EOA sequential flows, where the prior
  // approve is already mined) and single-call batches.
  //
  // Multiple calls: replay the batch's preceding calls first via `eth_simulateV1`
  // so their state is applied before the failing call. Nodes without simulate
  // support throw, and we fail open (no decoded name → no retry gate).
  try {
    const rawData =
      candidate.calls.length > 1
        ? await simulateBatchRevertData({
            calls: candidate.calls,
            publicClient,
            account,
            blockNumber,
            signal
          })
        : await callRevertData({
            targetCall,
            publicClient,
            account,
            blockNumber,
            signal
          })

    return normalizeRevertData(rawData)
  } catch (error) {
    if (isExecutionAbortError(error)) throw error
    return undefined
  }
}

async function callRevertData({
  targetCall,
  publicClient,
  account,
  blockNumber,
  signal
}: {
  targetCall: ResolvedCall
  publicClient: Client
  account: Account | Address
  blockNumber: bigint
  signal?: AbortSignal
}): Promise<Hex | undefined> {
  try {
    await raceAbort(
      call(publicClient, {
        account,
        blockNumber,
        data: targetCall.data,
        to: targetCall.to,
        value: targetCall.value
      }),
      signal
    )
    return undefined
  } catch (error) {
    if (isExecutionAbortError(error)) throw error
    if (!(error instanceof BaseError)) return undefined
    return extractRevertData(error)
  }
}

async function simulateBatchRevertData({
  calls,
  publicClient,
  account,
  blockNumber,
  signal
}: {
  calls: ResolvedCall[]
  publicClient: Client
  account: Account | Address
  blockNumber: bigint
  signal?: AbortSignal
}): Promise<Hex | undefined> {
  try {
    const { results } = await raceAbort(
      simulateCalls(publicClient, {
        account,
        blockNumber,
        calls: calls.map(resolvedCall => ({
          to: resolvedCall.to,
          data: resolvedCall.data,
          value: resolvedCall.value
        }))
      }),
      signal
    )
    // `results` maps 1:1 to the input calls; the last is the failing call whose
    // revert we decode. Its `data` carries the raw revert bytes on failure.
    //
    // Only trust the tail revert if every *preceding* call succeeded:
    // `eth_simulateV1` keeps executing later calls after an earlier one fails,
    // so a failed approve/authorize prefix would still let the tail revert with
    // a book-moved error — but the real atomic on-chain tx would have reverted
    // with the prefix failure, not the tail. Fail open when the prefix didn't
    // cleanly apply so we never misattribute a prefix failure to the take.
    const failingResult = results.at(-1)
    if (!failingResult || failingResult.status !== 'failure') return undefined
    if (results.slice(0, -1).some(result => result.status !== 'success')) return undefined
    return failingResult.data
  } catch (error) {
    if (isExecutionAbortError(error)) throw error
    return undefined
  }
}

function extractRevertData(error: BaseError): Hex | undefined {
  const rawError = error.walk(
    (candidate): candidate is RawContractError => candidate instanceof RawContractError
  )
  if (rawError instanceof RawContractError) {
    if (typeof rawError.data === 'string') return normalizeRevertData(rawError.data)
    return normalizeRevertData(rawError.data?.data)
  }

  const revertedError = error.walk(
    (candidate): candidate is ContractFunctionRevertedError =>
      candidate instanceof ContractFunctionRevertedError
  )
  if (revertedError instanceof ContractFunctionRevertedError) {
    return normalizeRevertData(revertedError.raw)
  }

  const hexDataError = error.walk((candidate): candidate is { data: Hex } => {
    if (typeof candidate !== 'object' || candidate === null) return false
    if (!('data' in candidate)) return false
    const data = (candidate as { data?: unknown }).data
    return typeof data === 'string' && data.startsWith('0x')
  })
  if (hexDataError) {
    const data = (hexDataError as { data?: unknown }).data
    if (typeof data === 'string') return normalizeRevertData(data as Hex)
  }

  return undefined
}

function normalizeRevertData(data: Hex | undefined): Hex | undefined {
  if (!data || data === '0x') return undefined
  return data
}
